/**
 * Browser glue for the contractor portal theme — storage + DOM apply.
 * Resolution rules live in `@tenfour/contracts` (pure, unit-tested).
 */
import {
  THEME_STORAGE_KEY,
  parseThemePreference,
  resolveTheme,
  themeAppliesToPath,
  type ResolvedTheme,
  type ThemePreference,
} from '@tenfour/contracts'

export {
  THEME_STORAGE_KEY,
  inlineWordmarkSrc,
  textWordmarkSrc,
  parseThemePreference,
  resolveTheme,
  themeAppliesToPath,
  toggleThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@tenfour/contracts'

export function readThemePreference(): ThemePreference {
  try {
    return parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY))
  } catch {
    return 'system'
  }
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
    else localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Quota / privacy mode — preference won't survive a reload.
  }
}

export function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** Apply (or clear) the document theme for the current route. */
export function applyDocumentTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
  pathname: string,
): ResolvedTheme {
  const resolved = themeAppliesToPath(pathname)
    ? resolveTheme(preference, systemPrefersDark)
    : 'light'

  const root = document.documentElement
  root.dataset.theme = resolved
  root.style.colorScheme = resolved
  return resolved
}
